/**
 * ImageFrame3D.js - Loads a 3D frame model and displays images on it
 * This provides a more reliable way to show images in 3D space
 */
import * as THREE from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";

export class ImageFrame3D {
    constructor(scene, camera) {
        this.scene = scene;
        this.camera = camera;
        this.frame = null;
        this.imagePlane = null;
        this.isVisible = false;
        this.isLoaded = false;
        this.frameModelPath = "/static/models/frames/picture_frame.fbx";

        // 🔹 Position of the whole frame group in the scene
        this.defaultPosition = new THREE.Vector3(0, 3.5, -3);

        // 🔹 GLOBAL SIZE CONTROLS
        // Change these to scale the frame and image
        this.frameScaleFactor = 5.5; // Overall frame size (FBX + plane)
        this.imageBaseHeight = 1.4; // Height of the image area (world units)

        // Default scale is derived from frameScaleFactor
        this.defaultScale = new THREE.Vector3(
            this.frameScaleFactor,
            this.frameScaleFactor,
            this.frameScaleFactor,
        );

        // Create a group to hold the frame and image
        this.group = new THREE.Group();
        this.scene.add(this.group);
        this.group.visible = false;

        // Load the frame model
        this.loadFrameModel();

        // Optional: Enable ESC-to-close
        this.setupKeyboardEvents();
    }

    /**
     * Load the frame model
     */
    loadFrameModel() {
        console.log(`Loading frame model from: ${this.frameModelPath}`);

        // Create a fallback frame if model loading fails
        const createFallbackFrame = () => {
            console.log("Creating fallback frame");

            // Base dimensions for the frame (unscaled, before group scale)
            const frameWidth = 1.6;
            const frameHeight = 1.1;
            const frameDepth = 0.08;

            const frameGeometry = new THREE.BoxGeometry(
                frameWidth,
                frameHeight,
                frameDepth,
            );
            const frameMaterial = new THREE.MeshPhongMaterial({
                color: 0xa67d3d, // Wood color
                specular: 0x222222,
                shininess: 25,
            });
            this.frame = new THREE.Mesh(frameGeometry, frameMaterial);

            // Image dimensions - will be adjusted on texture load too
            const imageHeight = this.imageBaseHeight;
            const imageWidth = imageHeight * (4 / 3); // temporary AR

            const imageGeometry = new THREE.PlaneGeometry(
                imageWidth,
                imageHeight,
            );
            const imageMaterial = new THREE.MeshBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 1,
            });
            this.imagePlane = new THREE.Mesh(imageGeometry, imageMaterial);
            this.imagePlane.position.z = 0.05; // Slightly in front of frame

            // Add both to the group
            this.group.add(this.frame);
            this.group.add(this.imagePlane);

            // Position the group
            this.group.position.copy(this.defaultPosition);
            this.group.scale.copy(this.defaultScale);

            // Look at the camera
            this.updateRotation();

            this.isLoaded = true;
            console.log("Fallback frame created");
        };

        try {
            const loader = new FBXLoader();
            loader.load(
                this.frameModelPath,
                (fbx) => {
                    console.log("Frame model loaded successfully");
                    this.frame = fbx;

                    // Add the frame to our group
                    this.group.add(this.frame);

                    // Position and scale the whole group
                    this.group.position.copy(this.defaultPosition);
                    this.group.scale.copy(this.defaultScale);

                    // 🔹 Create the image plane (size will be refined after texture load)
                    const imageHeight = this.imageBaseHeight;
                    const imageWidth = imageHeight * (4 / 3); // temp AR

                    const imageGeometry = new THREE.PlaneGeometry(
                        imageWidth,
                        imageHeight,
                    );
                    const imageMaterial = new THREE.MeshBasicMaterial({
                        color: 0xffffff,
                        transparent: true,
                        opacity: 1,
                    });
                    this.imagePlane = new THREE.Mesh(
                        imageGeometry,
                        imageMaterial,
                    );
                    this.imagePlane.position.z = 0.05; // Slightly in front of frame

                    // Add image plane to group
                    this.group.add(this.imagePlane);

                    // Make it face the camera
                    this.updateRotation();

                    this.isLoaded = true;
                    console.log("Frame model setup complete");
                },
                (xhr) => {
                    const percentComplete = (xhr.loaded / xhr.total) * 100;
                    console.log(
                        `Frame model loading: ${Math.round(percentComplete)}% complete`,
                    );
                },
                (error) => {
                    console.error("Error loading frame model:", error);
                    createFallbackFrame();
                },
            );
        } catch (error) {
            console.error("Error setting up frame loader:", error);
            createFallbackFrame();
        }
    }

    /**
     * Show an image on the frame
     * @param {string} imageUrl - URL of the image to display
     * @param {string} description - Description text
     * @returns {Promise<boolean>} - Whether the image was successfully displayed
     */
    async showImage(imageUrl, description) {
        console.log(`Showing image in 3D frame: ${imageUrl}`);

        if (!this.isLoaded) {
            console.log("Frame not loaded yet, waiting...");
            await new Promise((resolve) => {
                const checkLoaded = () => {
                    if (this.isLoaded) {
                        resolve();
                    } else {
                        setTimeout(checkLoaded, 100);
                    }
                };
                checkLoaded();
            });
        }

        try {
            // Load the image as a texture
            const texture = await this.loadTexture(imageUrl);

            // Apply texture to the image plane
            if (this.imagePlane && this.imagePlane.material) {
                this.imagePlane.material.map = texture;
                this.imagePlane.material.needsUpdate = true;

                // 🔹 Calculate aspect ratio and adjust image plane size
                const aspectRatio = texture.image.width / texture.image.height;

                const imageHeight = this.imageBaseHeight; // main control
                const imageWidth = imageHeight * aspectRatio;

                this.imagePlane.geometry.dispose();
                this.imagePlane.geometry = new THREE.PlaneGeometry(
                    imageWidth,
                    imageHeight,
                );
            }

            // Make the group visible
            this.group.visible = true;
            this.isVisible = true;

            // Make it face the camera
            this.updateRotation();

            // Animate the frame appearing
            this.animateAppear();

            console.log("Image displayed on 3D frame successfully");
            return true;
        } catch (error) {
            console.error("Error showing image on 3D frame:", error);
            return false;
        }
    }

    /**
     * Load a texture with CORS handling
     * @param {string} url - URL of the image
     * @returns {Promise<THREE.Texture>} - Loaded texture
     */
    loadTexture(url) {
        return new Promise((resolve, reject) => {
            const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
            console.log("Loading image through proxy:", proxyUrl);

            const loader = new THREE.TextureLoader();
            loader.crossOrigin = "anonymous";

            loader.load(
                proxyUrl,
                (texture) => {
                    console.log("Texture loaded successfully through proxy!");
                    resolve(texture);
                },
                (progress) => {
                    if (progress.lengthComputable) {
                        const percent =
                            (progress.loaded / progress.total) * 100;
                        if (percent % 25 < 1) {
                            console.log(
                                `Texture loading: ${Math.round(percent)}% complete`,
                            );
                        }
                    }
                },
                (error) => {
                    console.error(
                        "Error loading texture through proxy:",
                        error,
                    );

                    const canvas = document.createElement("canvas");
                    canvas.width = 512;
                    canvas.height = 512;
                    const context = canvas.getContext("2d");

                    const gradient = context.createLinearGradient(
                        0,
                        0,
                        canvas.width,
                        canvas.height,
                    );
                    gradient.addColorStop(0, "#6a11cb");
                    gradient.addColorStop(1, "#2575fc");
                    context.fillStyle = gradient;
                    context.fillRect(0, 0, canvas.width, canvas.height);

                    context.font = "24px Arial";
                    context.fillStyle = "white";
                    context.textAlign = "center";
                    context.fillText(
                        "Image could not be loaded",
                        canvas.width / 2,
                        canvas.height / 2,
                    );
                    context.fillText(
                        "Please try again later",
                        canvas.width / 2,
                        canvas.height / 2 + 30,
                    );

                    const fallbackTexture = new THREE.CanvasTexture(canvas);
                    console.log("Created fallback texture");
                    resolve(fallbackTexture);
                },
            );
        });
    }

    /**
     * Update frame rotation to face the camera
     */
    updateRotation() {
        if (!this.group) return;
        this.group.lookAt(this.camera.position);
    }

    /**
     * Animate the frame appearing
     */
    animateAppear() {
        const originalScale = this.defaultScale.clone();

        // Start small and grow to originalScale
        this.group.scale.set(0.1, 0.1, 0.1);

        const duration = 800; // ms
        const startTime = performance.now();

        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeProgress = 1 - Math.pow(1 - progress, 3);

            const currentScale = originalScale
                .clone()
                .multiplyScalar(easeProgress);

            this.group.scale.copy(currentScale);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.group.scale.copy(originalScale);
                console.log("Frame appear animation complete");
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Hide the frame
     */
    hide() {
        if (!this.isVisible) return;

        const duration = 300; // ms
        const startTime = performance.now();
        const originalScale = this.group.scale.clone();

        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const scale = originalScale.clone().multiplyScalar(1 - progress);
            this.group.scale.copy(scale);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.group.visible = false;
                this.isVisible = false;
                console.log("Frame hidden");

                // Reset for next time
                this.group.scale.copy(this.defaultScale);
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Update the frame's position/rotation based on camera movement
     */
    update() {
        if (this.isVisible) {
            this.updateRotation();
        }
    }

    /**
     * Set up keyboard event to close the frame with Escape key
     */
    setupKeyboardEvents() {
        window.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.isVisible) {
                this.hide();
            }
        });
    }
}
